import React, { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { fetchOwners } from '../../../helpers/prospect_api';
import { Col, Row, Spinner } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCoins } from '@fortawesome/pro-regular-svg-icons';
import pluralize from 'pluralize';
import AiThinking from './ai_thinking';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import type { OwnershipsResponse } from 'bundles/connect/types';
import { connect } from 'react-redux';

type Props = {
  insightsPropertyId: string;
  remainingCredits: number;
  setRemainingCredits: React.Dispatch<React.SetStateAction<number>>;
  trialUser: boolean;
  onUnlock: (results: OwnershipsResponse) => void;
};

function UnlockOwners({
  insightsPropertyId,
  remainingCredits,
  trialUser,
  onUnlock,
  setRemainingCredits
}: Props) {
  const [loading, setLoading] = useState(true);
  const [aiThinking, setAiThinking] = useState(false);
  const [restrictedProperty, setRestrictedProperty] = useState(false);

  const mobileView = useMediaQuery({
    query: '(max-width: 992px)'
  });

  const setOwners = useCallback((useCredit = false, includeAi = false) => {
    if (includeAi) {
      setAiThinking(true);
    } else {
      setLoading(true);
    }

    fetchOwners(insightsPropertyId, useCredit, false, includeAi)
      .then((results) => {
        onUnlock(results);
      })
      .catch((error) => {
        setLoading(false);
        setAiThinking(false);

        if (error.response && error.response.status === 422) {
          if (error.response.data['remaining_credits'] !== undefined) {
            setRemainingCredits(error.response.data['remaining_credits']);
          } else {
            setRestrictedProperty(true);
          }
        }
      });
  }, [insightsPropertyId]);

  useEffect(() => {
    setOwners();
  }, [insightsPropertyId]);

  if (aiThinking) {
    return <AiThinking />;
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const messageBody = () => {
    if (restrictedProperty) return (
      <Row className="flex-column gap-3">
        <Col>
          <div className="fs-large fw-bolder">Owner contacts for this property are not included in your plan.</div>
          Looks like you don't have access to this property.
          View and manage your subscription details from your account settings.
        </Col>
        <Col>
          <Button href="/prospect/profile#subscription" label="View Your Plan Details" variant="secondary" />
        </Col>
      </Row>
    );

    if (remainingCredits > 0) return (
      <div className={`bg-light d-flex rounded p-3 gap-5 ${mobileView ? 'flex-column' : ''}`}>
        <div className="d-flex gap-3">
          <FontAwesomeIcon className="pt-2" icon={faCoins} />
          <div>
            <div className="fs-large fw-bolder">Use a credit to unlock the owner contacts for this property.</div>
            You have {remainingCredits} {pluralize('credit', remainingCredits)} available
            {trialUser ? ' during your trial' : ' this billing cycle'}.&nbsp;
            If contact information is found for this property, it will cost 1 credit.
          </div>
        </div>

        <div className={`d-flex gap-2 ${mobileView ? 'flex-column' : 'flex-column align-items-end text-nowrap'}`}>
          <Button
            className="w-100"
            id="unlock_owners"
            label="Quick Lookup"
            variant="outline"
            onClick={() => setOwners(true, false)}
          />
          <div className="d-flex flex-column">
            <Button
              icon={faSparkles}
              id="unlock_owners_with_ai"
              label="In-depth Owner Lookup"
              variant="secondary"
              onClick={() => setOwners(true, true)}
            />
            <small className="text-center">Use AI to research contacts of this property.</small>
            <small className="text-center">Increases search time</small>
          </div>
        </div>
      </div>
    );

    return (
      <Row className="flex-column gap-3">
        <Col>
          <div className="fs-large fw-bolder">You have no credits remaining</div>
        </Col>
      </Row>
    );
  };

  return (
    <Row>
      {(restrictedProperty || (remainingCredits < 1)) &&
        <Col xs="auto">
          <FontAwesomeIcon className="p-4 bg-light border rounded" icon={faLock} />
        </Col>
      }
      <Col className="mx-2">
        {messageBody()}
      </Col>
    </Row>
  );
}

const mapStateToProps = ({ ui: { profile: { trialUser } } }) => ({ trialUser });
export default connect(mapStateToProps)(UnlockOwners);
